import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ArrowPathIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { flexRender, getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, RowModel, SortingState, Table, useReactTable } from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useApiHelper from "../../../hooks/useApiHelper";
import { PageMode } from "../../../models/PagedTable";
import { DefaultResponseWithData } from "../../../models/system";
import { ButtonInput } from "../Inputs/ButtonInput";
import LoadingWheel from "../LoadingWheel";
import apiConfig from "../../../utils/apiConfig";
;


export interface IPagedResponse<T> extends DefaultResponseWithData<IPagedData<T>> { }

export interface IPagedData<T> {
  rows: T[];
  total: number;
  totalPages: number;
  page: number;
  pageSize: number;
}

export interface IPagedTableActionButton<T> {
  label: string;
  onClick: (rows: RowModel<T>, table: Table<T>) => void;
  disabledOnNoRows?: boolean;
  isLoading?: boolean;
  classes?: string;
}

export interface IPagedTableQueryButton {
  label: string;
  onClick: (query: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

export interface IPagedModeToggle {
  label: string;
  pageMode?: PageMode;
  urlQuery?: string;
}

export type OtherInfo = {
  label: string;
  value: string;
}

export interface IPagedTableProps<T> {
  url: string;
  columns: any;
  pageMode?: PageMode;
  queryLabel?: string;
  externalQuery?: string;
  modeToggles?: IPagedModeToggle[];
  customUrlQuery?: string;
  actionButtons?: IPagedTableActionButton<T>[];
  queryButton?: IPagedTableQueryButton;
  expandedRow?: (row: T) => JSX.Element;
  extraRow?: (row: T) => JSX.Element;
  extraRowPosition?: "top" | "bottom";
  onRowClick?: (row: T) => void;
  onRowDoubleClick?: (row: T) => void;
  showTotal?: boolean;
  additionalBody?: Record<string, any>;
  initialSort?: SortingState;
  setRowData?: (data: any) => void;
  rowColor?: (row: T) => string;
  otherInfo?: OtherInfo[];
  onTotalRowsChange?: (totalRows: number) => void;
}

export type DateRangeType = {
  fieldName: string;
  start: Date | null;
  end: Date | null;
}

const EMPTY_ROWS: any[] = [];

export default function PagedTable<T>(tableConfig: IPagedTableProps<T>) {
  const { t } = useTranslation("common");
  const { post } = useApiHelper(apiConfig("PagedTable"));
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearchParam = searchParams.get("search") || "";
  const [query, setQuery] = useState<string>(urlSearchParam);
  const hasExternalQuery = tableConfig.externalQuery !== undefined;
  const [toggles, setToggles] = useState<boolean[]>(new Array(tableConfig.modeToggles ? tableConfig.modeToggles?.length : 0).fill(false));
  const [sorting, setSorting] = useState<SortingState>(tableConfig.initialSort ?? []);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { setRowData, onTotalRowsChange } = tableConfig;

  const debouncedQuery = useDebounce(query, 1000);
  const debouncedAdditionalBody = useDebounce(tableConfig.additionalBody, 300);
  const pageParams = [debouncedQuery, pageIndex, toggles, sorting, pageSize];

  const getPageMode = useCallback(() => {
    const activeModes = tableConfig.modeToggles
      ?.filter((toggle, i) => toggle.pageMode && toggles[i])
      .map(toggle => toggle.pageMode);

    // If no modes active, return default; otherwise a joined string of all modes
    return !activeModes || activeModes.length === 0
      ? tableConfig.pageMode
      : activeModes.join(',');
  }, [tableConfig, toggles]);

  const currentPageMode = useMemo(() => getPageMode(), [getPageMode]);

  // Cleanup function to cancel pending requests
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const applyQuery = useCallback((newQuery: string, resetPage: boolean = true) => {
    setQuery(newQuery);
    if (resetPage) setPageIndex(1);

    // Keep URL in sync for deep linking/back-forward behaviour
    const newSearchParams = new URLSearchParams(searchParams);
    if (newQuery.trim()) {
      newSearchParams.set("search", newQuery);
    } else {
      newSearchParams.delete("search");
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sync internal query from either external query prop or URL
  useEffect(() => {
    if (hasExternalQuery) {
      const external = tableConfig.externalQuery ?? "";
      if (external !== query) {
        applyQuery(external);
      }
      return;
    }

    const urlSearch = searchParams.get("search") || "";
    if (urlSearch !== query) {
      setQuery(urlSearch);
    }
  }, [hasExternalQuery, tableConfig.externalQuery, searchParams, query, applyQuery]);


  const getUrl = useCallback(() => {
    const { url, customUrlQuery, modeToggles } = tableConfig;

    const queryString =
      modeToggles
        ?.filter(toggle => toggle.urlQuery)
        .map(toggle => toggle.urlQuery)
        .join("&") ?? "";

    const customQuery = customUrlQuery ? `&${customUrlQuery}` : "";
    const modeQuery = currentPageMode;

    let finalUrl = `${url}/${pageIndex}?pageMode=${modeQuery}`;

    if (pageSize) finalUrl += `&pageSize=${pageSize}`;

    // Add the queries if they exist
    if (debouncedQuery.trim()) finalUrl += `&query=${debouncedQuery}`;

    if (queryString) finalUrl += `&${queryString}`;

    // Append the custom query if provided
    finalUrl += customQuery;

    return finalUrl;
  }, [tableConfig, debouncedQuery, pageIndex, getPageMode, pageSize]);

  const getSort = useCallback(() => {
    let sort: any = {};

    if (sorting.length != 0) {
      let matchingCol = tableConfig.columns.filter((x: any) => x.id == sorting[0]?.id)[0];
      let key = matchingCol?.accessorKey ? matchingCol.accessorKey : sorting[0].id;

      sort = { ...sorting[0], id: key };
    }

    return sort;
  }, [sorting, tableConfig.columns]);


  const requestBody = useMemo(() => {
    return {
      ...getSort(),
      searchBody: {
        ...debouncedAdditionalBody
      }
    };
  }, [sorting, tableConfig.columns, debouncedAdditionalBody]);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ["pagedTable", tableConfig.url, currentPageMode, debouncedAdditionalBody, ...pageParams],
    ({ signal }) => {
      // Cancel any previous request
      cleanup();

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      return post<any>(getUrl(), requestBody, abortControllerRef.current.signal) as Promise<IPagedResponse<T>>;
    },
    {
      keepPreviousData: true,
      staleTime: 1000 * 60 * 2, // 2 minutes
      refetchInterval: 1000 * 60 * 5, // 5 minutes
      retry: 1, // Limit retries to prevent infinite loops
      retryDelay: 1000, // Wait 1 second before retrying
      onError: (error) => {
        console.error('PagedTable query error:', error);
      },
    },
  );

  useEffect(() => {
    if (data?.data.rows && setRowData) setRowData(data.data.rows);
  }, [data?.data.rows]);

  useEffect(() => {
    if (typeof data?.data.total === "number" && onTotalRowsChange) {
      onTotalRowsChange(data.data.total);
    }
  }, [data?.data.total, onTotalRowsChange]);

  const table = useReactTable({
    data: data?.data.rows ?? EMPTY_ROWS,
    columns: tableConfig.columns,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    // Whenever data rows change, clear selection for any deleted rows
    if (table.getState().rowSelection && data?.data?.rows) {
      const validRowIds = new Set(data.data.rows.map((_, i) => i.toString()));
      const currentSelection = table.getState().rowSelection;

      // If any selected IDs are no longer valid, reset
      const hasInvalid = Object.keys(currentSelection).some(
        (key) => !validRowIds.has(key)
      );
      if (hasInvalid) {
        table.resetRowSelection();
      }
    }
  }, [data?.data?.rows, table]);

  useEffect(() => {
    if (data && !isLoading) {
      table.setPageSize(data.data.pageSize);
    }
  }, [data?.data?.pageSize, isLoading]);

  // useEffect(() => {
  //   console.log("Sorting changed:", getSort());
  // }, [sorting]);

  const changeQuery = (newQuery: string) => {
    applyQuery(newQuery);
  };

  const changePage = (increment: number) => {
    if (isLoading) return;
    setPageIndex(pageIndex + increment);
  }

  const handleToggle = (index: number) => {
    if (isLoading) return;

    let updatedToggles = [...toggles];

    // Flip the toggle state, allowing multiple to be active at once
    updatedToggles[index] = !updatedToggles[index];

    setToggles(updatedToggles);
  }

  // useEffect(() => {
  //   let rows = table.getRowModel();
  // }, [table.getSelectedRowModel()]);

  const lastUrlRef = useRef<string | null>(null);
  const lastBodyRef = useRef<string | null>(null);

  const memoizedUrl = useMemo(() => getUrl(), [getUrl]);
  const memoizedBody = useMemo(() => JSON.stringify(requestBody), [requestBody]);

  useEffect(() => {
    if (data && !isLoading && !isFetching) {
      lastUrlRef.current = memoizedUrl;
      lastBodyRef.current = memoizedBody;
    }
  }, [data, isLoading, isFetching, memoizedUrl, memoizedBody]);

  // Determine if we should show loading
  const shouldShowLoading = isLoading ||
    (isFetching && lastUrlRef.current !== memoizedUrl) ||
    (isFetching && lastBodyRef.current !== memoizedBody);


  // Determine if we should show error state
  const shouldShowError = error && !data?.data?.rows?.length && !isLoading;

  // Check if error is an abort error (which is expected during cleanup)
  const isAbortError = error && (
    (error as any)?.name === 'AbortError' ||
    (error as any)?.message?.includes('aborted')
  );

  // Show error state only if we have a real error and no previous data
  if (shouldShowError && !isAbortError) {
    return (
      <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-800">{t("Error loading data. Please try again.")}</p>
        <ButtonInput
          label={t("Retry")}
          onClick={() => refetch()}
          classes="mt-2"
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mt-4 flex justify-between">

        <div className="flex gap-x-5">

          {tableConfig.queryLabel ? (
            <div className="flex items-stretch gap-2">
              <div className="relative flex items-stretch flex-grow focus-within:z-10">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
                <input
                  type="text"
                  value={query}
                  disabled={isLoading}
                  onChange={(e) => changeQuery(e.target.value)}
                  className="pl-10 sm:text-sm lg:min-w-[250px] sm:min-w-[unset] max-w-full lg:max-w-[calc(100% - 3rem)] border-1 border-gray-300 rounded-md focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder={tableConfig.queryLabel!}
                  style={{ width: `calc(${tableConfig.queryLabel!.length}ch - 2rem)` }}
                />
              </div>
              {tableConfig.queryButton && (
                <ButtonInput
                  label={tableConfig.queryButton.label}
                  isSubmit={false}
                  onClick={() => tableConfig.queryButton?.onClick(query)}
                  isLoading={tableConfig.queryButton.isLoading}
                  disabled={tableConfig.queryButton.disabled || isLoading}
                  classes="whitespace-nowrap"
                />
              )}
            </div>
          ) : null}

          {toggles?.map((toggle, i) =>
            <div className="flex items-center" key={i}>
              <input onChange={() => handleToggle(i)} checked={toggle} type="checkbox" className="mr-2 h-5 w-5" />
              <label >{tableConfig.modeToggles![i].label}</label>
            </div>
          )}

        </div>

        {/* {tableConfig.showTotal &&
          <div className="flex items-center gap-2">
            <div className="text-sm">Total Rows: {data?.data.total}</div>
          </div>
        } */}

        <div className="flex items-end gap-2 flex-col">
          {tableConfig.showTotal && <div className="text-sm">{t("Total Rows:")} {data?.data.total}</div>}
          {tableConfig.otherInfo && tableConfig.otherInfo.length > 0 && tableConfig.otherInfo.map((info, i) =>
            <div className="text-sm" key={i}>{info?.label}: {(data?.data as any)?.[info?.value]}</div>
          )}
        </div>

      </div>
      <div className="flex gap-4 mt-3">
        {tableConfig.actionButtons?.map((button, i) =>
          button.isLoading ?
            <ButtonInput key={i} disabled={true} isSubmit={false} onClick={() => button.onClick(table.getSelectedRowModel(), table)} classes={button.classes ?? "!bg-primary-600 px-3 py-2 text-white"}>
              <ArrowPathIcon className="animate-spin h-5 w-5" />
            </ButtonInput>
            :
            <ButtonInput key={i} label={button.label} disabled={button.disabledOnNoRows ? table.getSelectedRowModel().rows.length === 0 : false} isSubmit={false} onClick={() => button.onClick(table.getSelectedRowModel(), table)} classes={button.classes ?? ""} />
        )}
      </div>
      <div className="mt-4 bg-white overflow-x-auto overflow-y-hidden">
        <div className="flex justify-between items-center gap-2 py-4">

          <div className="pl-4 w-1/3">
            {t("{{count}} Rows", { count: table.getPreFilteredRowModel()?.rows?.length })}
            {table.getSelectedRowModel().rows.length > 0 && (
              <span className="ml-2 text-primary-700 text-xs">
                <i>
                  ({t("{{count}} selected", { count: table.getSelectedRowModel().rows.length })})
                </i>
              </span>
            )}
          </div>

          <div className="flex gap-2  w-1/3">
            <ButtonInput label={"<"} isSubmit={false} onClick={() => changePage(-1)}
              disabled={!(pageIndex > 1)} classes="text-xl"
            />

            <ButtonInput label={">"} isSubmit={false} onClick={() => changePage(1)}
              disabled={!(pageIndex < data?.data?.totalPages!)} classes="text-xl"
            />

            <span className="flex items-center gap-1  w-1/3">
              <div>{t("Page")}</div>
              <strong>
                {data?.data.page} {t("of")}{' '}
                {data?.data.totalPages}
              </strong>
            </span>
          </div>

          <div className="flex gap-4 pr-4">

            <span className="flex items-center gap-1">
              {t("Go to page:")}
              <input
                type="number"
                defaultValue={pageIndex}
                onChange={e => {
                  // unsure why -1 was here, but it meant that the page was always off by 1; just an oscar thing ig
                  // let page = e.target.value ? Number(e.target.value) - 1 : 0;
                  let page = e.target.value ? Number(e.target.value) : 0;
                  page = page < 1 || page > data?.data.totalPages! ? 1 : page;
                  setPageIndex(page);
                }}
                className="border p-1 rounded w-16"
              />
            </span>

            <select
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value))
              }}
              className="border p-1 rounded w-32"
            >
              {[10, 30, 50, 100, 200, 1000].map(size => (
                <option key={size} value={size}>
                  {t("Show {{count}}", { count: size })}
                </option>
              ))}
            </select>
          </div>
        </div>

        <table className="min-w-full overflow-scroll divide-y border divide-gray-300">
          <thead>
            {table.getHeaderGroups().map((headerGroup, i) => (
              <tr key={`headerGroup-${headerGroup.id}-${i}`} className="bg-primary-50 text-gray-800">
                {headerGroup.headers.map((header, j) => (
                  <th
                    key={`header-${header.id}-${j}`}
                    scope="col"
                    className={`py-3.5 px-3 text-sm font-semibold lg:table-cell text-left ${(header.column.columnDef.meta as any)?.thClassName ?? ""}`}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={
                          header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : ""
                        }
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: " ▲",
                          desc: " ▼",
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {/* Loading state keeps its own tbody */}
          {shouldShowLoading && (
            <tbody>
              <tr>
                <td colSpan={12}>
                  <div className="flex justify-center py-4">
                    <LoadingWheel />
                  </div>
                </td>
              </tr>
            </tbody>
          )}

          {/* Actual rows */}
          {!shouldShowLoading &&
            table.getRowModel().rows.map((row, index) => {
              const rowCells = row.getVisibleCells();
              // Ensure unique key by combining row.id (which is unique from TanStack Table) with index as fallback
              const rowKey = `row-${row.id}-${index}`;

              return (
                <tbody key={rowKey} className="group">
                  {/* Extra row (top) */}
                  {tableConfig.extraRow && tableConfig.extraRowPosition === "top" && (
                    <tr
                      key={`extra-${rowKey}`}
                      onClick={() => tableConfig.onRowClick?.(row.original)}
                      className={`group-hover:bg-gray-100 ${tableConfig.onRowClick ? "hover:cursor-pointer" : ""} ${tableConfig.rowColor?.(row.original) ?? ""}`}
                    >
                      <td className="border-r" />
                      <td colSpan={rowCells.length}>
                        {tableConfig.extraRow(row.original)}
                      </td>
                    </tr>
                  )}

                  {/* Main row */}
                  <tr
                    onClick={() => tableConfig.onRowClick?.(row.original)}
                    onDoubleClick={() => tableConfig.onRowDoubleClick?.(row.original)}
                    className={`group-hover:bg-gray-100 ${tableConfig.onRowClick || tableConfig.onRowDoubleClick ? "hover:cursor-pointer" : ""} !${tableConfig.rowColor?.(row.original) ?? ""
                      }`}
                  >
                    {rowCells.map((cell, i) => (
                      <td key={`cell-${cell.id}-${i}`} className={`px-3 py-3 border text-sm ${(cell.column.columnDef.meta as any)?.tdClassName ?? ""}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>

                  {/* Extra row (bottom — default) */}
                  {tableConfig.extraRow && tableConfig.extraRowPosition !== "top" && (
                    <tr
                      key={`extra-${rowKey}`}
                      onClick={() => tableConfig.onRowClick?.(row.original)}
                      className={`group-hover:bg-gray-100 ${tableConfig.onRowClick ? "hover:cursor-pointer" : ""} ${tableConfig.rowColor?.(row.original) ?? ""
                        }`}
                    >
                      <td className="border-r" />
                      <td colSpan={rowCells.length}>
                        {tableConfig.extraRow(row.original)}
                      </td>
                    </tr>
                  )}

                  {/* Expanded row */}
                  {row.getIsExpanded() && (
                    <tr
                      key={`expanded-${rowKey}`}
                      className="group-hover:bg-gray-100">
                      <td colSpan={rowCells.length}>
                        {tableConfig.expandedRow?.(row.original)}
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })}
        </table>
      </div>
    </div>
  );
}